import { Anchor, Badge, Card, Group, SimpleGrid, Stack, Text, ThemeIcon, Title, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconArrowRight,
  IconBell,
  IconCalendarQuestion,
  IconChecklist,
  IconCircleCheck,
  IconClipboardCheck,
  IconClockHour4,
  IconFileCheck,
  IconInbox,
  IconMessage2,
  IconMessageQuestion,
  IconSend,
  type Icon,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { businessToday, type TaskWorklistCountDto } from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { useMyTasks, useRequestDashboard } from '@/features/requests/api';
import { DueDate, TaskStatusBadge } from '@/features/requests/components/badges';
import { notificationKeys } from '@/layout/NotificationBell';

function greeting(): string {
  const hour = new Date().getHours();
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}

/** A count that opens the list it counts. Worklists that need action take their colour only when they have any. */
function Stat({ label, value, to, icon: Icon, color = 'brand', hint }: { label: string; value: number | string | undefined; to: string; icon: Icon; color?: string; hint?: string }) {
  return (
    <Card component={Link} to={to} padding="md" style={{ textDecoration: 'none', color: 'inherit' }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Text fz="sm" c="dimmed">
            {label}
          </Text>
          <Title order={2} className="tabular">
            {value ?? '—'}
          </Title>
          {hint && (
            <Text fz="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </Stack>
        <ThemeIcon size={44} radius="md" variant="light" color={color}>
          <Icon size={24} stroke={1.7} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function Section({ title, description, link, children }: { title: string; description?: string; link?: { to: string; label: string }; children: ReactNode }) {
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
        <Group gap="xs" align="baseline">
          <Text fw={600}>{title}</Text>
          {description && (
            <Text fz="sm" c="dimmed">
              {description}
            </Text>
          )}
        </Group>
        {link && (
          <Anchor component={Link} to={link.to} fz="sm" fw={600}>
            <Group gap={4}>
              {link.label} <IconArrowRight size={16} />
            </Group>
          </Anchor>
        )}
      </Group>
      {children}
    </Stack>
  );
}

const inRequests = (w: TaskWorklistCountDto | undefined) => (w ? `in ${w.requests} request${w.requests === 1 ? '' : 's'}` : undefined);
const attention = (n: number | undefined, color: string) => (n ? color : 'gray');

/**
 * The staff dashboard, as the work flows now. Coordinators see the requests by the stage the client sees them at
 * (new, in process, under RA review, with BD, completed) and the worklists that need someone's action: client queries
 * waiting for compliance, clarifications waiting for the client, uploads to verify, returned and overdue tasks, date
 * proposals. Everyone with tasks sees their own and what is next.
 */
export function DashboardPage() {
  const user = useCurrentUser();
  const { can } = useAuth();
  const canTasks = can('MyTasks');
  const canRequests = can('DossierRequests');

  const unread = useQuery({ queryKey: notificationKeys.unread, queryFn: () => http.get<{ count: number }>('/notifications/unread-count') });
  const tasks = useMyTasks('open');
  const board = useRequestDashboard(canRequests);
  const b = board.data;

  const openTasks = canTasks ? (tasks.data ?? []) : [];
  const today = businessToday();
  const overdue = openTasks.filter((t) => t.item.status !== 'submitted' && t.item.targetDate && t.item.targetDate < today).length;
  const upcoming = [...openTasks].filter((t) => t.item.status !== 'submitted').slice(0, 5);

  return (
    <>
      <PageHeader title={`${greeting()}, ${user.fullName.split(' ')[0]}`} description="Here is where things stand today." />
      <Stack gap="lg">
        {canRequests && (
          <Section title="Requisitions" description="by the stage the client sees" link={{ to: '/internal/requests', label: 'All requests' }}>
            <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 5 }}>
              <Stat label="New" value={b?.stages.received} hint="No task assigned yet" to="/internal/requests?stage=received" icon={IconInbox} color="gray" />
              <Stat label="In process" value={b?.stages.inProcess} hint="Tasks or a client query open" to="/internal/requests?stage=in_preparation" icon={IconClockHour4} />
              <Stat
                label="Under RA review"
                value={b?.stages.underReview}
                hint="All verified: release or send to BD"
                to="/internal/requests?stage=final_review"
                icon={IconClipboardCheck}
                color="violet"
              />
              <Stat label="With BD" value={b?.withBd} hint="Waiting for SC-NBD in BDS" to="/internal/requests?view=with_bd" icon={IconSend} color="violet" />
              <Stat label="Completed" value={b?.completed} hint="Released or approved" to="/internal/requests?view=completed" icon={IconCircleCheck} color="teal" />
            </SimpleGrid>
          </Section>
        )}

        {canRequests && (
          <Section title="Needs action">
            <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }}>
              <Stat
                label="Open client queries"
                value={b?.openQueries}
                hint="Waiting for your compliance"
                to="/internal/requests?flag=open_queries"
                icon={IconMessage2}
                color={attention(b?.openQueries, 'grape')}
              />
              <Stat
                label="Waiting for the client"
                value={b?.awaitingClient}
                hint="Clarifications not answered yet"
                to="/internal/requests?flag=awaiting_client"
                icon={IconMessageQuestion}
                color={attention(b?.awaitingClient, 'orange')}
              />
              <Stat
                label="Uploads to verify"
                value={b?.toVerify.tasks}
                hint={inRequests(b?.toVerify)}
                to="/internal/requests?flag=to_verify"
                icon={IconFileCheck}
                color={attention(b?.toVerify.tasks, 'yellow')}
              />
              <Stat
                label="Returned for rework"
                value={b?.returned.tasks}
                hint={inRequests(b?.returned)}
                to="/internal/requests?flag=returned"
                icon={IconArrowBackUp}
                color={attention(b?.returned.tasks, 'orange')}
              />
              <Stat
                label="Overdue tasks"
                value={b?.overdue.tasks}
                hint={inRequests(b?.overdue)}
                to="/internal/requests?flag=overdue"
                icon={IconAlertTriangle}
                color={attention(b?.overdue.tasks, 'red')}
              />
              <Stat
                label="Date proposals to review"
                value={b?.dateProposals.tasks}
                hint={inRequests(b?.dateProposals)}
                to="/internal/requests?flag=date_proposals"
                icon={IconCalendarQuestion}
                color={attention(b?.dateProposals.tasks, 'yellow')}
              />
            </SimpleGrid>
          </Section>
        )}

        <Section title="You">
          <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }}>
            {canTasks && (
              <Stat
                label="My open tasks"
                value={tasks.data?.length}
                to="/internal/my-tasks"
                icon={IconChecklist}
                hint={overdue ? `${overdue} overdue` : undefined}
                color={overdue ? 'orange' : 'brand'}
              />
            )}
            <Stat label="Unread notifications" value={unread.data?.count} to="/internal/notifications" icon={IconBell} color="gray" />
          </SimpleGrid>
        </Section>

        {canTasks && upcoming.length > 0 && (
          <Card>
            <Group justify="space-between" mb="sm">
              <Group gap="xs">
                <Text fw={600}>Next up</Text>
                {overdue > 0 && (
                  <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
                    {overdue} overdue
                  </Badge>
                )}
              </Group>
              <UnstyledButton component={Link} to="/internal/my-tasks" c="brand" fz="sm" fw={600}>
                <Group gap={4}>
                  All my tasks <IconArrowRight size={16} />
                </Group>
              </UnstyledButton>
            </Group>
            <Stack gap={0}>
              {upcoming.map((t) => (
                <UnstyledButton key={t.item.id} component={Link} to={`/internal/my-tasks/${t.item.id}`} py="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                  <Group justify="space-between" wrap="nowrap">
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text fz="sm" fw={600} truncate="end">
                        {t.item.title}
                      </Text>
                      <Text fz="xs" c="dimmed" truncate="end">
                        {t.request.productName} for {t.request.country}
                      </Text>
                    </Stack>
                    <Group gap="sm" wrap="nowrap">
                      <DueDate date={t.item.targetDate} compact />
                      <TaskStatusBadge status={t.item.status} />
                    </Group>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </>
  );
}
